/**
 * Nuke Page Component - Emergency Eject Button
 * 
 * A page containing a large, fun emergency eject button for emergency use only.
 * The button is centered on the page with an eye-catching design.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function NukePage() {
  const [isPressed, setIsPressed] = useState(false);
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');

  const handleButtonClick = () => {
    setShowPasscodeDialog(true);
    setPasscode('');
    setError('');
  };

  const handlePasscodeSubmit = () => {
    if (passcode.toUpperCase() === 'ERIC') {
      setShowPasscodeDialog(false);
      setPasscode('');
      setError('');
      // Execute emergency action
      console.log('Emergency eject button activated!');
      // Functionality will be implemented later
    } else {
      setError('Incorrect passcode');
      setPasscode('');
    }
  };

  const handlePasscodeChange = (value: string) => {
    // Only allow letters and max 4 characters
    const letters = value.replace(/[^a-zA-Z]/g, '').slice(0, 4).toUpperCase();
    setPasscode(letters);
    setError('');
    
    // Auto-submit when 4 characters are entered
    if (letters.length === 4) {
      setTimeout(() => {
        if (letters === 'ERIC') {
          setShowPasscodeDialog(false);
          setPasscode('');
          setError('');
          console.log('Emergency eject button activated!');
        } else {
          setError('Incorrect passcode');
          setPasscode('');
        }
      }, 100);
    }
  };

  const buttonVariants = {
    rest: {
      y: 0,
      scale: 1,
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 60px 10px rgba(239, 68, 68, 0.3), inset 0 -8px 16px rgba(0, 0, 0, 0.3)',
    },
    hover: {
      y: -8,
      scale: 1.02,
      boxShadow: '0 35px 60px -12px rgba(0, 0, 0, 0.9), 0 0 80px 20px rgba(239, 68, 68, 0.5), inset 0 -8px 16px rgba(0, 0, 0, 0.3)',
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    pressed: {
      y: 4,
      scale: 0.98,
      boxShadow: '0 8px 20px -8px rgba(0, 0, 0, 0.9), 0 0 40px 5px rgba(239, 68, 68, 0.6), inset 0 4px 12px rgba(0, 0, 0, 0.5)',
      transition: {
        duration: 0.1,
        ease: 'easeIn',
      },
    },
  };

  const glowVariants = {
    rest: {
      opacity: 0.4,
      scale: 1,
    },
    hover: {
      opacity: [0.5, 0.7, 0.5],
      scale: [1.05, 1.15, 1.05],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
    pressed: {
      opacity: 0.9,
      scale: 0.95,
      transition: {
        duration: 0.1,
      },
    },
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex flex-col items-center justify-center bg-black -mx-4 sm:-mx-6 lg:-mx-8 -my-8 p-8">
      {/* Page Title */}
      <motion.h1
        className="text-4xl md:text-5xl font-bold text-white mb-16 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        For Emergency Use Only
      </motion.h1>

      {/* Emergency Eject Button Container */}
      <motion.div
        className="relative"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {/* Outer Glow Ring */}
        <motion.div
          className="absolute inset-0 rounded-full pointer-events-none"
          variants={glowVariants}
          initial="rest"
          whileHover="hover"
          animate={isPressed ? 'pressed' : 'rest'}
          style={{
            background: 'radial-gradient(circle, rgba(239, 68, 68, 0.5) 0%, rgba(239, 68, 68, 0.2) 40%, transparent 70%)',
            filter: 'blur(30px)',
          }}
        />

        {/* Button */}
        <motion.button
          className="relative w-64 h-64 md:w-80 md:h-80 rounded-full flex items-center justify-center group cursor-pointer focus:outline-none focus:ring-4 focus:ring-red-500/50 focus:ring-offset-4 focus:ring-offset-black overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
          }}
          variants={buttonVariants}
          initial="rest"
          whileHover="hover"
          whileTap="pressed"
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          onMouseLeave={() => setIsPressed(false)}
          onClick={handleButtonClick}
        >
          {/* Outer Ring/Border */}
          <div className="absolute inset-0 rounded-full border-[12px] border-red-900/80" />
          
          {/* Inner Ring Highlight */}
          <div className="absolute inset-[12px] rounded-full border-2 border-red-400/20" />

          {/* Top Highlight - 3D lighting effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-b from-white/15 via-transparent to-transparent" 
               style={{ clipPath: 'ellipse(50% 30% at 50% 20%)' }} />

          {/* Bottom Shadow - depth effect */}
          <motion.div 
            className="absolute inset-0 rounded-full bg-gradient-to-t from-black/40 via-transparent to-transparent"
            animate={isPressed ? { opacity: 0.7 } : { opacity: 0.4 }}
            transition={{ duration: 0.1 }}
          />

          {/* Inner Shadow for pressed state */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at 50% 40%, transparent 30%, rgba(0,0,0,0.3) 100%)',
            }}
            animate={isPressed ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.1 }}
          />

          {/* Center dome effect */}
          <div className="absolute inset-[15%] rounded-full bg-gradient-to-br from-red-500/30 to-transparent" />

          {/* Button Text */}
          <div className="relative z-10 text-center">
            <motion.div
              className="text-white font-bold text-3xl md:text-4xl mb-2 tracking-wider drop-shadow-lg"
              animate={isPressed ? { y: 2, scale: 0.98 } : { y: 0, scale: 1 }}
              transition={{ duration: 0.1 }}
            >
              EJECT
            </motion.div>
            <motion.div
              className="text-white/90 text-lg md:text-xl font-semibold drop-shadow-lg"
              animate={isPressed ? { y: 2, scale: 0.98 } : { y: 0, scale: 1 }}
              transition={{ duration: 0.1 }}
            >
              NUKE
            </motion.div>
          </div>

          {/* Decorative Warning Ring */}
          <motion.div 
            className="absolute inset-0 rounded-full border-4 border-yellow-400/30"
            style={{ transform: 'scale(1.08)' }}
            animate={isPressed ? { 
              borderColor: 'rgba(250, 204, 21, 0.5)',
            } : {
              borderColor: 'rgba(250, 204, 21, 0.3)',
            }}
          />
        </motion.button>
      </motion.div>

      {/* Warning Text */}
      <motion.p
        className="mt-12 text-gray-400 text-lg text-center max-w-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        This button will execute emergency procedures. Use with extreme caution.
      </motion.p>

      {/* Passcode Dialog */}
      <AnimatePresence>
        {showPasscodeDialog && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPasscodeDialog(false)}
            />

            {/* Dialog */}
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-gray-900 border-2 border-red-600 rounded-2xl p-8 max-w-md w-full shadow-2xl"
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                transition={{ type: 'spring', duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Dialog Title */}
                <div className="text-center mb-6">
                  <div className="text-red-500 text-5xl mb-4">⚠️</div>
                  <h2 className="text-2xl font-medium text-white mb-2">
                    Authorization Required
                  </h2>
                  <p className="text-gray-400">
                    Enter 4-character passcode to proceed
                  </p>
                </div>

                {/* Passcode Input */}
                <div className="mb-6">
                  <input
                    type="text"
                    maxLength={4}
                    value={passcode}
                    onChange={(e) => handlePasscodeChange(e.target.value)}
                    className="w-full text-center text-3xl font-bold tracking-[0.5em] bg-gray-800 border-2 border-gray-700 rounded-lg py-4 px-6 text-white uppercase focus:outline-none focus:border-red-500 transition-colors"
                    placeholder="****"
                    autoFocus
                  />
                  
                  {/* Error Message */}
                  <AnimatePresence>
                    {error && (
                      <motion.p
                        className="text-red-500 text-center mt-3 font-semibold"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                      >
                        {error}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {/* Passcode Dots Indicator */}
                <div className="flex justify-center gap-3 mb-6">
                  {[0, 1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      className={`w-4 h-4 rounded-full border-2 ${
                        passcode.length > i
                          ? 'bg-red-500 border-red-500'
                          : 'border-gray-600'
                      }`}
                      animate={{
                        scale: passcode.length === i ? [1, 1.3, 1] : 1,
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPasscodeDialog(false)}
                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePasscodeSubmit}
                    disabled={passcode.length !== 4}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

